import { Add, Clear } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import {
  AlertsBarContainer,
  AlertsBarVariant,
  AlertsContext,
} from "@sista/dista-ui";
import {
  Profile,
  Relation,
  Right,
  SistaDialog,
  SistaFormHeader,
  UiContext,
  useSafeSistaAsync,
} from "@sista/library-stasi";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useRejstrikClient } from "../../../api/utils/useRejstrikClient";
import { ConfigContext } from "../../../hooks/ConfigContext";

type OrganizationRolesDialogProps = {
  profile: Profile;
  organizationId: string;
  onDone: () => unknown;
};

export const OrganizationRolesDialog = (
  props: OrganizationRolesDialogProps,
) => {
  const [relations, setRelations] = useState([
    ...((props.profile as any)._viewData.relations as Relation[]).filter(
      (r) => r.refEntityId == props.organizationId && r.to == null,
    ),
  ]);

  const { closeDialogs } = useContext(UiContext);
  const { showInfo } = useContext(AlertsContext);

  const { filterProfileRoles, roleName } = useContext(ConfigContext);
  const { t, i18n } = useTranslation("ucet");

  const { addProfileRoleInOrganization, deleteProfileRoleInOrganization } =
    useRejstrikClient();

  const addProfileRoleImpl = useCallback(
    async (role: string) => {
      const rel = {
        organizationId: props.organizationId,
        profileId: props.profile.id,
        changeReason: "role added from /ucet",
        minorChange: false,
        data: {
          role: role,
          entityId: props.profile.id,
          entityType: "profiles",
          refEntityId: props.organizationId,
          refEntityType: "organizations",
        },
      };
      const result = await addProfileRoleInOrganization(rel);

      setRelations([
        ...relations,
        {
          id: result.value,
          ...rel.data,
        },
      ]);
    },
    [
      addProfileRoleInOrganization,
      props.organizationId,
      props.profile.id,
      relations,
    ],
  );
  const [addProfileRoleCall, addProfileRoleState] =
    useSafeSistaAsync(addProfileRoleImpl);

  const deleteProfileRoleImpl = useCallback(
    async (role: string) => {
      if (relations == null || relations.length == 1) {
        showInfo(
          t(
            "ucet.organization.mustHaveOneRole",
            "Uživatel musí mít alespoň jednu roli.",
          ),
        );
        return;
      }
      await deleteProfileRoleInOrganization({
        organizationId: props.organizationId,
        profileId: props.profile.id,
        role: role,
        changeReason: "role removed from /ucet",
        minorChange: false,
        data: role,
      });
      setRelations(relations.filter((r) => r.role !== role));
    },
    [
      deleteProfileRoleInOrganization,
      props.organizationId,
      props.profile.id,
      relations,
      showInfo,
      t,
    ],
  );
  const [deleteProfileRoleCall, deleteProfileRoleState] = useSafeSistaAsync(
    deleteProfileRoleImpl,
  );

  const possibleRoles = useMemo(() => {
    const rs = filterProfileRoles("organizations").filter(
      (r) =>
        r.role === "ORGANIZATION_MANAGER" || r.role === "ORGANIZATION_EMPLOYEE",
    );
    return [...rs.filter((r) => !relations.find((rel) => rel.role === r.role))];
  }, [filterProfileRoles, relations]);

  const done = useCallback(() => {
    props.onDone();
    closeDialogs();
  }, [closeDialogs, props]);

  const pending = deleteProfileRoleState.pending || addProfileRoleState.pending;

  return (
    <SistaDialog
      title={t("ucet.organization.rolesDialogTitle", "Role")}
      onClose={done}
    >
      <AlertsBarContainer
        id="alerts-in-dialog"
        variant={AlertsBarVariant.relative}
      />
      <SistaFormHeader
        title={props.profile.firstName + " " + props.profile.lastName}
        subtitle={t(
          "ucet.organization.rolesSubtitle",
          "Přidejte nebo odeberte role, které uživatel zastává.",
        )}
      />
      <Divider />
      <Stack direction="column" spacing={2}>
        <Typography variant={"h3"} marginTop={2}>
          {t("ucet.roles.profileRoles", "Existující role uživatele")}
        </Typography>
        <Box>
          {relations.map((r) => (
            <span key={r.id}>
              <Chip
                deleteIcon={<Clear />}
                onDelete={async () => !pending && deleteProfileRoleCall(r.role)}
                label={roleName(r.role, i18n.language)}
                sx={{ mr: 1 }}
              />{" "}
            </span>
          ))}

          {(!relations || relations.length == 0) && (
            <Typography variant={"body2"}>
              {t("ucet.roles.noRoles", "Uživatel nemá žádné relevantní role")}
            </Typography>
          )}
        </Box>
        <Divider />
        <Typography variant={"h3"}>
          {t("ucet.roles.possibleRoles", "Možné role")}
        </Typography>
        <Box>
          {possibleRoles.map((r) => (
            <span key={r.role}>
              <Chip
                deleteIcon={<Add />}
                onDelete={async () => !pending && addProfileRoleCall(r.role)}
                label={roleName(r.role, i18n.language)}
                sx={{ mr: 1 }}
              />{" "}
            </span>
          ))}
        </Box>
        {(!possibleRoles || possibleRoles.length == 0) && (
          <Typography variant={"body2"}>
            {t(
              "ucet.roles.noPossibleRoles",
              "V SISTA nejsou další role, které by šlo uživateli přiřadit.",
            )}
          </Typography>
        )}
        <Right>
          <LoadingButton loading={pending} onClick={done} variant="contained">
            {t("common.done", "Hotovo")}
          </LoadingButton>
        </Right>
      </Stack>
    </SistaDialog>
  );
};
